import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import {
  PartObject,
  PartConditionDropdown,
  PartLocationObject,
  PartListResponse,
  PartHistoryDataType,
  NSPartLocationList,
  SerailizedPartsList,
  PartHistoryMovementListResponse,
  PartHistoryMovementListFilterOptions,
  MerchantDamageTemplateQuery,
  MerchantDamagePartSerialQueryData,
  DuplicatePartsDataType,
  PartHistory,
  UpdatePartLocationObject,
} from '../@types/part.type';
import {
  getAllWarrantySummary,
  getPartById,
  getPartConditions,
  getPartExistanceBySerials,
  getNSPartExistanceBySerials,
  getPartHistoryByPartId,
  getPartLocationDataById,
  updatePart,
  updatePartLocation,
  getPartLocationDataByServiceId,
  updateBulkPart,
  getPartMovementHistory,
  getPartHistoryByServiceIdAndHistoryTypeCodes,
  getDuplicatePartsBySerial1AndPartType,
  mergeDuplicateParts,
  createExceptionalPart,
  updateExceptionalPart,
  getPartRepairHistory,
  getLatestPartMomentHistory,
  getPartByIdForService,
  getPartConditionsForService,
  getPartLocationDataByIdForService,
  updatePartForService,
  processPartBulkUpdateFile,
  getPartLocationDataByPartId,
  getExistingSerials
} from '../api/part';
import { WarrantyPanalType } from '../@types/warrantySummary.type';
import { QueryOption } from '../@types';
import { PartTypeLocationItemData } from '../components/molecules/PartTypeLocationItem';
import {
  getAllParts,
  getNSPartByPartTypeAndLocation,
  getPartsLogAgainstService,
  getMerchantDamagePartSerialData,
  updatePartAdditionalData
} from '../api/parts';
import {
  PartKey,
  PartConditionKey,
  WarrantySummaryKey,
  PartLocationKey,
  PartsKey,
  PartHistoryKey,
  PartExistanceKey,
  NSPartExistanceKey,
  NSPartLocationListKey,
  PartLocationsListKey,
  MerchantDamagePartSerialDataKey,
  PartHistoryListForServiceKey,
  DuplicatePartsKey,
  LatestPartMomentHistory
} from '../configs/part';
import { ServiceLogAllKey } from '../configs/services';
import { PartActionHistoryDataType } from '../@types/partHistory.type';
import { getSelectedColumnNames } from '../utils/columnSelectionOperations';
import { ColumnSelectionItem } from '../components/templates/ColumnSelection';

export const useGetOnePart = (id: number): UseQueryResult<PartObject> => {
  return useQuery({
    queryKey: [PartKey, id],
    queryFn: () => id && getPartById(id)
  });
};

export const useGetOnePartForService = (id: number): UseQueryResult<PartObject> => {
  return useQuery({
    queryKey: [PartKey, id],
    queryFn: () => id && getPartByIdForService(id)
  });
};

export const useGetPartConditions = (): UseQueryResult<[PartConditionDropdown]> => {
  return useQuery({
    queryKey: [PartConditionKey],
    queryFn: () => getPartConditions(),
    staleTime: 5 * 60 * 60 * 1000
  });
};

export const useGetPartConditionsForService = (): UseQueryResult<[PartConditionDropdown]> => {
  return useQuery({
    queryKey: [PartConditionKey],
    queryFn: () => getPartConditionsForService(),
    staleTime: 5 * 60 * 60 * 1000
  });
};

export const useUpdatePart = (id: number): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await updatePart(data.id, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PartKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [PartHistoryKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [WarrantySummaryKey, id]
      });
    }
  });
};

export const useUpdatePartForService = (id: number): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await updatePartForService(data.id, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PartKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [PartHistoryKey, id]
      });
    }
  });
};

export const useGetAllWarrantySummary = (id: number): UseQueryResult<[WarrantyPanalType]> => {
  return useQuery({
    queryKey: [WarrantySummaryKey, id],
    queryFn: () => id && getAllWarrantySummary(id)
  });
};

export const useGetPartLocationDataById = (id: number): UseQueryResult<PartLocationObject> => {
  return useQuery({
    queryKey: [PartLocationKey, id],
    queryFn: () => id && getPartLocationDataById(id)
  });
};

export const useGetPartLocationDataByPartId = (id: number, locationData: UpdatePartLocationObject): UseQueryResult<PartLocationObject> => {
  return useQuery({
    queryKey: [PartLocationKey, id],
    queryFn: () => id && getPartLocationDataByPartId(id, locationData),
    enabled: false
  });
};

export const useGetPartLocationDataByIdForService = (id: number): UseQueryResult<PartLocationObject> => {
  return useQuery({
    queryKey: [PartLocationKey, id],
    queryFn: () => id && getPartLocationDataByIdForService(id)
  });
};

export const useGetPartLocationDataByServiceId = (partTypeId: number, serviceId: number): UseQueryResult<[PartTypeLocationItemData]> => {
  return useQuery({
    queryKey: [PartLocationsListKey, partTypeId, serviceId],
    queryFn: () => getPartLocationDataByServiceId(partTypeId, serviceId)
  });
};

export const useUpdatePartLocation = (id: number): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await updatePartLocation(data.id, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PartLocationKey, id]
      });
    }
  });
};

export const useGetAllParts = (queryOption: QueryOption, serialSearch?: string, columnSelectionItems?: ColumnSelectionItem[], obfuscation?: string): UseQueryResult<PartListResponse> => {
  return useQuery({
    queryKey: [PartsKey, queryOption, obfuscation],
    queryFn: () => getAllParts(queryOption, serialSearch, getSelectedColumnNames(columnSelectionItems), obfuscation),
    gcTime: 0,
    enabled: false
  });
};

export const useGetPartHistoryByPartId = (id: number): UseQueryResult<[PartHistoryDataType]> => {
  return useQuery({
    queryKey: [PartHistoryKey, id],
    queryFn: () => getPartHistoryByPartId(id)
  });
};

export const useGetPartExistanceBySerials = (): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await getPartExistanceBySerials(data);
      return response;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PartExistanceKey]
      });
    }
  });
};

export const useGetNSPartExistanceBySerials = (): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await getNSPartExistanceBySerials(data);
      return response;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [NSPartExistanceKey]
      });
    }
  });
};

export const useGetNSPartByPartTypeAndLocation = (partTypeId: number, locationId: number, isReturn: boolean = false, isIncludeLocationChildren: boolean = false): UseQueryResult<NSPartLocationList[]> => {
  return useQuery({
    queryKey: [NSPartLocationListKey, partTypeId, locationId],
    queryFn: () => getNSPartByPartTypeAndLocation(partTypeId, locationId, isReturn, isIncludeLocationChildren),
    enabled: false
  });
};

export const useUpdateBulkParts = (): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await updateBulkPart(data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PartsKey]
      });
    }
  });
};

export const useGetPartsLogAgainstService = (serviceId: number, locationTypeCode: string): UseQueryResult<[SerailizedPartsList]> => {
  return useQuery({
    queryKey: [PartKey, serviceId],
    queryFn: () => getPartsLogAgainstService(serviceId, locationTypeCode)
  });
};

export const useGetPartMovementHistory = (queryOption: PartHistoryMovementListFilterOptions): UseQueryResult<PartHistoryMovementListResponse> => {
  return useQuery({
    queryKey: [PartHistoryKey, queryOption],
    queryFn: () => (
      (queryOption.startDate && queryOption.endDate) ||
      queryOption.partId ||
      queryOption?.filterModel?.items?.some((filter) => filter?.field === 'createdAt')
    ) && getPartMovementHistory(queryOption),
    enabled: false,
    gcTime: 0
  });
};

export const useGetMerchantDamagePartSerialDataQuery = (queryOption: MerchantDamageTemplateQuery): UseQueryResult<[MerchantDamagePartSerialQueryData]> => {
  return useQuery({
    queryKey: [MerchantDamagePartSerialDataKey],
    queryFn: () => getMerchantDamagePartSerialData(queryOption),
    gcTime: 0
  });
};

export const useUpdatePartAdditionalData = (partId: number): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await updatePartAdditionalData(data, partId);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey]
      });
    }
  });
};
export const useGetPartHistoryByServiceIdAndHistoryTypeCodes = (serviceId: number, historyTypes: string[]): UseQueryResult<Array<PartActionHistoryDataType>> => {
  return useQuery({
    queryKey: [PartHistoryListForServiceKey, serviceId],
    queryFn: () => getPartHistoryByServiceIdAndHistoryTypeCodes(serviceId, historyTypes)
  });
};

export const useGetDuplicatePartsBySerial1AndPartType = (mainPartId: number, serial1: string, partTypeId: number): UseQueryResult<Array<DuplicatePartsDataType>> => {
  return useQuery({
    queryKey: [DuplicatePartsKey, mainPartId],
    queryFn: () => mainPartId && serial1 && partTypeId && getDuplicatePartsBySerial1AndPartType(mainPartId, serial1, partTypeId)
  });
};

export const useGetPartRepairHistory = (partId: number): UseQueryResult<PartHistory[]> => {
  return useQuery({
    queryKey: [PartsKey, partId],
    queryFn: () => getPartRepairHistory(partId)
  });
};

export const useGetLatestPartMomentHistory = (partId: number): UseQueryResult<PartHistory> => {
  return useQuery({
    queryKey: [LatestPartMomentHistory, partId],
    queryFn: () => getLatestPartMomentHistory(partId)
  });
};

export const useMergeDuplicateParts = (mainPartId: number): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await mergeDuplicateParts(data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [WarrantySummaryKey, mainPartId]
      });
      await queryClient.invalidateQueries({
        queryKey: [PartHistoryKey]
      });
      await queryClient.invalidateQueries({
        queryKey: [DuplicatePartsKey, mainPartId]
      });
    }
  });
};

export const useCreateExceptionalPart = (): UseMutationResult => {
  return useMutation({
    mutationFn: async (data: any) => {
      return await createExceptionalPart(data);
    }
  });
};

export const useUpdateExceptionalPart = (): UseMutationResult => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: any) => {
      return await updateExceptionalPart(data.id, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PartHistoryKey]
      });
    }
  });
};

export const useProcessPartBulkUpdateFile = (): UseMutationResult => {
  return useMutation({
    mutationFn: async (fileList: any) => {
      return await processPartBulkUpdateFile(fileList);
    }
  });
};

export const useGetExistingSerials = (): UseMutationResult => { 
  return useMutation({
    mutationFn: async (data: any) => {
      return await getExistingSerials(data);
    }
  });
};